// init.schema.json source — request file consumed by `avatar autopilot init
// --request-file <path>` (spec §3.3 command table: "Validate init.schema gồm
// blueprint bytes/hash, repo/base, draft artifacts, resource names và actor
// binding; khởi tạo idempotent qua initializer. Không nhận approval tự khai
// từ model; tạo gate pending và trả mission ID/receipt.").
import { z } from "zod";

/** V2 §4.4 deploy targets (startbuild `a7-deploy`). */
export const DEPLOY_TARGETS = ["local-only", "qa", "staging", "production"] as const;
export type DeployTarget = (typeof DEPLOY_TARGETS)[number];

export const InitRequestSchema = z
  .object({
    schema_version: z.literal(1),
    mode: z.enum(["greenfield", "brownfield"]),
    repo: z
      .object({
        source_repo: z.string().min(1),
        base_branch: z.string().min(1),
        base_sha: z.string().min(1),
      })
      .strict(),
    blueprint: z
      .object({
        source_path: z.string().min(1),
        bytes_sha256: z.string().regex(/^[0-9a-f]{64}$/i),
      })
      .strict(),
    draft_artifacts: z.array(
      z
        .object({
          path: z.string().min(1),
          sha256: z.string().regex(/^[0-9a-f]{64}$/i),
        })
        .strict(),
    ),
    // P25 §8 condition 4 ("đụng ranh giới scope"). The resource boundary,
    // declared by the PERSON running init — a worker can never write it.
    // `initMission` persists it (mission-declared-resources.ts) and the G2
    // decision measures the plan against it. An EMPTY list is a valid
    // declaration ("no external resources") but sends G2 to a human, because
    // "touches nothing outside the repo" is a claim worth one person's look.
    resource_names: z
      .array(z.string())
      .describe(
        "Tên các tài nguyên NGOÀI repo mission mà mission được phép đụng — " +
          'vd "mattermost:<channel>", "git-remote:<remote>", "supabase:<project-ref>", ' +
          '"s3:<bucket>". Người chạy `autopilot init` khai, worker KHÔNG ghi được. ' +
          "Dùng làm ranh giới scope cho G2 (§8 điều kiện 4). Khai rỗng [] là hợp lệ " +
          "(mission không đụng tài nguyên ngoài) nhưng G2 sẽ cần người duyệt.",
      ),
    actor_binding: z
      .object({
        // OS account / uid at setup — "recorded authority của chủ mission"
        // (spec §"Gate, danh tính và tài nguyên"). No approval self-claimed
        // by a model — this identifies the human requester only.
        os_account: z.string().min(1),
        bound_at: z.string().min(1),
      })
      .strict(),
    // V2 §4.4: optional + additive — startbuild fills it (from `a7-deploy`)
    // only when `autopilot capabilities` says `init_deploy_target`.
    deploy_target: z
      .enum(DEPLOY_TARGETS)
      .optional()
      .describe(
        "Môi trường đích người khai lúc init. Khác local-only + chưa có config.release ⇒ " +
          "`run` xong in gợi ý thêm release để xin G3. Không vào G3/hash.",
      ),
  })
  .strict();

export type InitRequest = z.infer<typeof InitRequestSchema>;
